import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { MARKS, expand, foldAt, lineNumbers, liveFolds, regionAt, unfold, type Fold } from './code-folding.js'
import './code-editor.css'

/**
 * The editor a print template is written in (SET-040).
 *
 * A template runs to three hundred lines, and the server reports a mistake by
 * its line, so the plain text box was the wrong tool: no line numbers to find
 * line 214 by, and no colour to tell the HTML from the expressions in it.
 *
 * Built as the usual overlay rather than pulled in as a dependency: a real
 * <textarea> on top — so typing, selection, undo, paste and screen readers all
 * behave exactly as they do in any other field — with its text made
 * transparent over a highlighted copy, and a gutter of line numbers kept in
 * step on scroll. What is stored is exactly what was typed; nothing here
 * rewrites the template.
 */
export interface CodeEditorProps {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly mode?: 'HANDLEBARS' | 'PUG'
  /** The line the server said is wrong, marked in the gutter. */
  readonly errorLine?: number | null
  readonly readOnly?: boolean
  readonly minHeight?: number
  readonly label?: string
}

const escape = (s: string): string => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string))

const span = (cls: string, text: string): string => `<span class="tok-${cls}">${escape(text)}</span>`

/** Colours `{{expressions}}` wherever they are, then the HTML around them. */
const highlightHtml = (src: string): string => {
  const out: string[] = []
  const re = /(\{\{\{?[\s\S]*?\}?\}\})|(<!--[\s\S]*?-->)|(<\/?[A-Za-z][^>]*>)/g
  let last = 0
  for (let m = re.exec(src); m; m = re.exec(src)) {
    out.push(escape(src.slice(last, m.index)))
    if (m[1]) out.push(span(/^\{\{[#/]|^\{\{else/.test(m[1]) ? 'block' : 'expr', m[1]))
    else if (m[2]) out.push(span('com', m[2]))
    else if (m[3]) out.push(tag(m[3]))
    last = m.index + m[0].length
  }
  out.push(escape(src.slice(last)))
  return out.join('')
}

const tag = (t: string): string => {
  const m = /^(<\/?)([A-Za-z][\w-]*)([\s\S]*?)(\/?>)$/.exec(t)
  if (!m) return escape(t)
  const attrs = (m[3] ?? '').replace(/([\w:-]+)(=)?("[^"]*"|'[^']*')?/g, (whole, name: string, eq?: string, value?: string) =>
    `\u0000${name}\u0001${eq ?? ''}${value ? `\u0002${value}\u0003` : ''}`)
  const coloured = escape(attrs)
    .replace(/\u0000([^\u0001]*)\u0001/g, '<span class="tok-attr">$1</span>')
    .replace(/\u0002([^\u0003]*)\u0003/g, '<span class="tok-str">$1</span>')
  return `${span('tag', `${m[1]}${m[2]}`)}${coloured}${span('tag', m[4] ?? '>')}`
}

/** PUG: the tag at the start of a line, keywords, interpolation, comments. */
const highlightPug = (src: string): string => src.split('\n').map((line) => {
  const comment = /^(\s*)(\/\/.*)$/.exec(line)
  if (comment) return `${comment[1]}${span('com', comment[2]!)}`
  const head = /^(\s*)(if|else if|else|unless|each|for|doctype)\b(.*)$/.exec(line)
  const tagged = /^(\s*)([A-Za-z][\w-]*)?((?:[.#][\w-]+)*)(.*)$/.exec(line)
  const interp = (s: string): string => escape(s).replace(/([#!]\{[^}]*\})/g, '<span class="tok-expr">$1</span>')
  if (head) return `${head[1]}${span('block', head[2]!)}${interp(head[3]!)}`
  if (tagged && (tagged[2] || tagged[3])) {
    return `${tagged[1]}${tagged[2] ? span('tag', tagged[2]) : ''}${tagged[3] ? span('attr', tagged[3]) : ''}${interp(tagged[4] ?? '')}`
  }
  return interp(line)
}).join('\n')

export const CodeEditor = ({
  value, onChange, mode = 'HANDLEBARS', errorLine = null, readOnly = false, minHeight = 420, label = 'Template',
}: CodeEditorProps) => {
  const gutter = useRef<HTMLDivElement>(null)
  const layer = useRef<HTMLPreElement>(null)

  /*
    Folding (see code-folding.ts): while anything is folded, the textarea shows
    `shown` — the template with the folded regions taken out — and every edit is
    expanded back into the whole template before it is handed on.
  */
  const [folds, setFolds] = useState<Fold[]>([])
  const [shown, setShown] = useState<string | null>(null)
  const text = folds.length > 0 && shown !== null ? shown : value
  // A template replaced from outside (opened, reset, converted) starts unfolded.
  useEffect(() => {
    if (shown !== null && expand(shown, folds) !== value) { setFolds([]); setShown(null) }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => text.split('\n'), [text])
  const numbers = useMemo(() => lineNumbers(text, folds), [text, folds])
  // A trailing newline needs something after it, or the overlay is a line short.
  const highlighted = useMemo(
    () => `${(mode === 'PUG' ? highlightPug(text) : highlightHtml(text))
      .replace(MARKS, '<span class="tok-fold">\u22ef</span>')}\n `,
    [text, mode],
  )

  const edit = (next: string): void => {
    if (folds.length === 0) { onChange(next); return }
    const live = liveFolds(next, folds)
    setFolds(live)
    setShown(live.length > 0 ? next : null)
    onChange(expand(next, live))
  }
  const toggle = (at: number): void => {
    const line = lines[at] ?? ''
    const mark = line.match(MARKS)?.[0]
    const result = mark ? unfold(text, folds, mark) : foldAt(text, folds, at)
    setFolds(result.folds)
    setShown(result.folds.length > 0 ? result.shown : null)
  }

  const syncScroll = (el: HTMLTextAreaElement): void => {
    if (layer.current) {
      layer.current.scrollTop = el.scrollTop
      layer.current.scrollLeft = el.scrollLeft
    }
    if (gutter.current) gutter.current.scrollTop = el.scrollTop
  }

  // Tab indents rather than leaving the field: indentation is the syntax in
  // PUG and the readability in HTML. Shift+Tab still leaves, for the keyboard.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key !== 'Tab' || e.shiftKey || readOnly) return
    e.preventDefault()
    const el = e.currentTarget
    const { selectionStart: start, selectionEnd: end } = el
    const next = `${text.slice(0, start)}  ${text.slice(end)}`
    edit(next)
    requestAnimationFrame(() => { el.selectionStart = start + 2; el.selectionEnd = start + 2 })
  }

  return (
    <div className="code-editor" style={{ minHeight }}>
      <div className="code-editor__gutter" ref={gutter}>
        {lines.map((line, i) => {
          const real = numbers[i] ?? i + 1
          const folded = /[\uE000-\uF8FF]/.test(line)
          const foldable = !folded && regionAt(lines, i) !== null
          return (
            <div key={i} className={errorLine === real ? 'code-editor__num code-editor__num--error' : 'code-editor__num'}>
              {folded || foldable ? (
                <button
                  type="button"
                  className="code-editor__fold"
                  aria-label={folded ? `Unfold line ${real}` : `Fold line ${real}`}
                  aria-expanded={!folded}
                  onClick={() => toggle(i)}
                >
                  {folded ? '\u25b8' : '\u25be'}
                </button>
              ) : <span className="code-editor__fold" aria-hidden="true" />}
              <span aria-hidden="true">{real}</span>
            </div>
          )
        })}
      </div>
      <div className="code-editor__body">
        <pre
          className="code-editor__layer"
          ref={layer}
          aria-hidden="true"
          // Escaped above, character by character; nothing typed reaches the DOM as markup.
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
        <textarea
          className="code-editor__input"
          aria-label={label}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          wrap="off"
          readOnly={readOnly}
          value={text}
          onChange={(e) => edit(e.currentTarget.value)}
          onScroll={(e) => syncScroll(e.currentTarget)}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  )
}

/** "… (line 214)" or "Line 214: …" in a server message → 214. */
export const errorLineOf = (message: string | null | undefined): number | null => {
  const m = /\b(?:line|PUG line)\s+(\d+)/i.exec(message ?? '')
  return m ? Number(m[1]) : null
}
